import * as Sentry from "@sentry/react";

// 500번대 에러 타입 정의
const SERVER_ERROR_TYPES = {
  500: { name: "internal_server_error", description: "Internal Server Error" },
  501: { name: "not_implemented", description: "Not Implemented" },
  502: { name: "bad_gateway", description: "Bad Gateway" },
  503: { name: "service_unavailable", description: "Service Unavailable" },
  504: { name: "gateway_timeout", description: "Gateway Timeout" },
  505: {
    name: "http_version_not_supported",
    description: "HTTP Version Not Supported",
  },
  506: {
    name: "variant_also_negotiates",
    description: "Variant Also Negotiates",
  },
  507: { name: "insufficient_storage", description: "Insufficient Storage" },
  508: { name: "loop_detected", description: "Loop Detected" },
  509: {
    name: "bandwidth_limit_exceeded",
    description: "Bandwidth Limit Exceeded",
  },
  510: { name: "not_extended", description: "Not Extended" },
  511: {
    name: "network_authentication_required",
    description: "Network Authentication Required",
  },
};

const parseObjToBodyString = (obj) => {
  return Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join("&");
};

// Sentry 에러 캡처 함수
const captureSentryError = (
  error,
  endpoint,
  method,
  additionalContext = {},
) => {
  const statusCode = error.status || error.statusCode;

  if (statusCode >= 500 && statusCode <= 599) {
    const errorType = SERVER_ERROR_TYPES[statusCode] || {
      name: `server_error_${statusCode}`,
      description: `Server Error ${statusCode}`,
    };

    Sentry.captureException(error, {
      tags: {
        error_category: "5xx_error",
        error_type: errorType.name,
        http_status: statusCode.toString(),
        http_status_group: "5xx",
        api_endpoint: endpoint,
        api_method: method,
        api_version: REACT_APP_VERSION_TYPE,
      },
      contexts: {
        server_error_details: {
          status_code: statusCode,
          error_name: errorType.name,
          error_description: errorType.description,
          is_timeout: statusCode === 504,
          is_unavailable: statusCode === 503,
        },
        api_request: {
          url: `${process.env.REACT_APP_API_URL}${endpoint}`,
          method: method,
          timestamp: new Date().toISOString(),
          ...additionalContext,
        },
      },
      level: statusCode === 503 || statusCode === 504 ? "warning" : "error",
    });
  }
};

let REACT_APP_VERSION_TYPE = process.env.REACT_APP_VERSION_TYPE;

const getAuthHeaders = () => {
  const user = localStorage.getItem("user");
  const userObj = JSON.parse(user);

  if (
    (userObj?.username === "hjmkay@gmail.com" ||
      userObj?.username === "jiojio7@gmail.com") &&
    REACT_APP_VERSION_TYPE === "stable"
  ) {
    return {
      "x-version": "canary",
    };
  } else {
    return { "x-version": REACT_APP_VERSION_TYPE };
  }
};

const api = {};

api.get = (endpoint, query = {}) => {
  let isJson = true;

  const URL = `${
    process.env.REACT_APP_API_URL
  }${endpoint}?${parseObjToBodyString(query)}`;
  return fetch(URL, {
    credentials: "include",
    mode: "cors",
    headers: {
      ...getAuthHeaders(),
    },
  })
    .then((res) => {
      var contentType = res.headers.get("content-type");
      if (contentType.includes("openxmlformats")) {
        isJson = false;
        return res.blob();
      } else {
        return res.json();
      }
    })
    .then((res) => {
      if (!isJson) {
        return res;
      }

      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "GET", { query });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "GET",
          },
        });
      }
      throw error;
    });
};

api.post = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "post",
    mode: "cors",
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "POST", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "POST",
          },
        });
      }
      throw error;
    });
};

api.put = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "put",
    mode: "cors",
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "PUT", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "PUT",
          },
        });
      }
      throw error;
    });
};

api.putFile = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body,
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
    },
    method: "PUT",
    mode: "cors",
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "PUT", {
          is_file_upload: true,
        });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "PUT_FILE",
          },
        });
      }
      throw error;
    });
};

api.delete = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "delete",
    mode: "cors",
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "DELETE", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "DELETE",
          },
        });
      }
      throw error;
    });
};

export default api;
